import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2,
  Phone,
  ArrowUpRight,
  ArrowRight,
  TrendingUp,
  Sparkles,
  Bot,
  Search,
  ShoppingBag,
  Briefcase,
  Building2,
  Globe,
  Shield,
  Target,
  Compass,
  AlertTriangle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import { LANDING } from "../constants/testIds";
import { WhatsappIcon } from "../components/icons/whatsapp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WA_NUMBER_RAW = "37129435194";
const WA_NUMBER_DISPLAY = "+371 29 435 194";
const WA_URL = `https://wa.me/${WA_NUMBER_RAW}`;
const TEL_URL = `tel:+${WA_NUMBER_RAW}`;

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

const scrollToForm = () => {
  const el = document.getElementById("pieteikties");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* -------------------- Tracking helpers -------------------- */
const trackWhatsAppClick = (location) => {
  const eventId = `wa_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq(
        "trackCustom",
        "whatsapp_cta",
        {
          location,
          content_name: "WhatsApp Click",
          content_category: "Contact",
        },
        { eventID: eventId }
      );
    }
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "whatsapp_cta", {
        event_category: "contact",
        event_label: location,
        transaction_id: eventId,
      });
    }
  } catch (e) {
    /* no-op */
  }
};

/* -------------------- NAV -------------------- */
const Nav = () => (
  <header className="sticky top-0 z-40 backdrop-blur bg-[#fafaf7]/85 border-b border-[#003e77]/12">
    <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
      <a href="#" data-testid={LANDING.navLogo} className="flex items-center gap-3 group">
        <img
          src="/favicon.png"
          alt="iMarketings.lv"
          className="h-7 w-7"
          loading="eager"
        />
        <span className="font-serif-display text-[19px] tracking-tight text-[#003e77]">
          iMarketings<span className="text-[#ed1c28]">.lv</span>
        </span>
        <span className="hidden md:inline-block h-4 w-px bg-[#003e77]/20" />
        <span className="hidden md:inline font-mono-accent text-[11px] tracking-[0.18em] uppercase text-[#003e77]/60">
          AI&nbsp;SEO&nbsp;Lab
        </span>
      </a>
      <div className="flex items-center gap-2">
        <a
          data-testid={LANDING.navWhatsappBtn}
          href={WA_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackWhatsAppClick("nav")}
          aria-label="Chat in WhatsApp"
          className="hidden md:inline-flex items-center justify-center h-10 w-10 rounded-[3px] border border-[#003e77]/15 text-[#003e77] hover:bg-[#003e77]/5 transition-colors"
        >
          <WhatsappIcon className="h-4 w-4" />
        </a>
        <button
          data-testid={LANDING.navApplyBtn}
          onClick={scrollToForm}
          className="inline-flex items-center gap-2 px-4 h-10 rounded-[3px] bg-[#ed1c28] text-white text-sm font-medium hover:bg-[#c8141e] transition-colors"
        >
          Pieteikties <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  </header>
);

/* -------------------- HERO -------------------- */
const Hero = () => (
  <section className="relative pt-14 pb-16 md:pt-20 md:pb-24">
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-8">
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-[#ed1c28]" />
            <span className="font-mono-accent text-[11px] tracking-[0.24em] uppercase text-[#003e77]/70">
              Bezmaksas · AI Visibility Audit · 2026
            </span>
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            custom={1}
            variants={fadeUp}
            className="font-serif-display text-balance text-[#003e77] text-[44px] sm:text-[58px] lg:text-[82px] leading-[1.02] tracking-[-0.02em]"
          >
            Bezmaksas <br className="hidden sm:block" />
            <span>AI SEO audits</span>
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            custom={2}
            variants={fadeUp}
            className="mt-7 max-w-2xl text-[17px] md:text-[19px] leading-[1.55] text-[#1a1a1a]/75"
          >
            Vai <span className="text-[#003e77] font-medium">ChatGPT</span>,{" "}
            <span className="text-[#003e77] font-medium">Claude</span>,{" "}
            <span className="text-[#003e77] font-medium">Google Gemini</span>{" "}
            iesaka Jūsu uzņēmumu vai konkurentus? Ja konkurentus, kāpēc ne Jūsu
            uzņēmumu? Ko darīt, lai AI iesaka Jūsu uzņēmumu? Piesakiet bezmaksas
            AI SEO auditu. Noskaidrojiet visu!
          </motion.p>

          <motion.div
            initial="hidden"
            animate="show"
            custom={3}
            variants={fadeUp}
            className="mt-10 flex flex-col sm:flex-row gap-3"
          >
            <button
              data-testid={LANDING.heroApplyBtn}
              onClick={scrollToForm}
              className="group inline-flex items-center justify-center gap-2 h-12 px-7 rounded-[3px] bg-[#ed1c28] text-white text-[15px] font-medium hover:bg-[#c8141e] transition-all"
            >
              Pieteikties bezmaksas auditam
              <ArrowUpRight className="h-5 w-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
            <a
              data-testid={LANDING.heroWhatsappBtn}
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick("hero")}
              aria-label="Chat in WhatsApp"
              className="inline-flex items-center justify-center h-12 w-12 rounded-[3px] border border-[#003e77]/20 text-[#003e77] hover:bg-[#003e77]/5 transition-colors"
            >
              <WhatsappIcon className="h-5 w-5" />
            </a>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            custom={4}
            variants={fadeUp}
            className="mt-12 space-y-4"
          >
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-[#1a1a1a]/65">
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#003e77]" /> Google Premier Partner
              </span>
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#003e77]" /> Meta Business Partner
              </span>
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#003e77]" /> Pieredze kopš 2013
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-[#1a1a1a]/65">
              <span className="inline-flex items-baseline gap-1.5">
                <span className="font-serif-display text-[#003e77] text-[15px] leading-none">
                  200+
                </span>
                <span>veiksmes stāsti</span>
              </span>
              <span className="text-[#003e77]/25">·</span>
              <span className="inline-flex items-baseline gap-1.5">
                <span className="font-serif-display text-[#003e77] text-[15px] leading-none">
                  919M EUR
                </span>
                <span>klientu ieņēmumi 2025 (GA4)</span>
              </span>
              <span className="text-[#003e77]/25">·</span>
              <span className="inline-flex items-baseline gap-1.5">
                <span className="font-serif-display text-[#003e77] text-[15px] leading-none">
                  LV · EU · USA
                </span>
                <span>kur virzām klientus</span>
              </span>
            </div>
          </motion.div>
        </div>

        {/* Video */}
        <div className="lg:col-span-4">
          <HeroVideo />
        </div>
      </div>
    </div>
  </section>
);

/* -------------------- HERO VIDEO -------------------- */
const HeroVideo = () => {
  const [playing, setPlaying] = useState(false);
  const videoRef = React.useRef(null);
  const play = () => {
    setPlaying(true);
    requestAnimationFrame(() => {
      const v = videoRef.current;
      if (v) {
        v.muted = false;
        v.play().catch(() => {});
      }
    });
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.75, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
      className="relative aspect-[9/16] max-w-[340px] mx-auto rounded-[6px] overflow-hidden bg-black shadow-[0_30px_60px_-15px_rgba(0,62,119,0.35)] ring-1 ring-[#003e77]/10"
      data-testid="hero-video"
    >
      <video
        ref={videoRef}
        src="/assets/sergejs-video.mp4"
        poster="/assets/video-poster.jpg"
        controls={playing}
        playsInline
        preload="metadata"
        className="w-full h-full object-cover"
      />
      {!playing && (
        <button
          onClick={play}
          aria-label="Atskaņot video"
          data-testid="hero-video-play-btn"
          className="absolute inset-0 flex items-center justify-center bg-black/15 hover:bg-black/5 transition-colors group"
        >
          <span className="inline-flex h-16 w-16 md:h-20 md:w-20 items-center justify-center rounded-full bg-[#ed1c28] text-white shadow-[0_10px_30px_rgba(237,28,40,0.5)] group-hover:scale-[1.08] transition-transform">
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              className="h-7 w-7 md:h-8 md:w-8 translate-x-0.5"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
          <span className="absolute bottom-4 left-4 right-4 text-center text-[11px] uppercase tracking-[0.2em] font-mono-accent text-white/85">
            ▸ 23 sek. no Sergeja
          </span>
        </button>
      )}
    </motion.div>
  );
};

/* -------------------- QUOTE -------------------- */
const Quote = () => (
  <section className="relative py-14 md:py-20 bg-[#003e77] text-white overflow-hidden">
    <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-[#ed1c28] opacity-[0.08] blur-[120px] translate-y-1/3 -translate-x-1/4" />
    <div className="relative max-w-7xl mx-auto px-5 md:px-10">
      <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center">
        {/* Photo */}
        <div className="md:col-span-4 lg:col-span-3">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative w-48 h-48 md:w-full md:h-auto md:aspect-square mx-auto"
          >
            <div className="absolute inset-0 rounded-full bg-white/5 border border-white/10" />
            <img
              src="/assets/sergejs.png"
              alt="Sergejs Volvenkins, PhD"
              className="relative w-full h-full object-cover object-top rounded-full"
              loading="lazy"
            />
          </motion.div>
        </div>

        {/* Quote */}
        <div className="md:col-span-8 lg:col-span-9">
          <div className="font-quote text-[#ed1c28] text-6xl leading-none mb-2 select-none">
            &ldquo;
          </div>
          <motion.blockquote
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-quote text-[22px] sm:text-[26px] md:text-[30px] leading-[1.4] tracking-[-0.01em] text-white"
          >
            Aizvien biežāk klienti jautājumu uzdod nevis Google, bet ChatGPT, un
            ja atbildē parādīsies konkurents, nevis Jūsu uzņēmums, Jūs par šo
            klientu pat neuzzināsiet. Viņš neatnāca un neaizgāja. Viņš vienkārši
            neuzzināja, ka Jūs eksistējat.
          </motion.blockquote>
          <div className="mt-7 flex items-center gap-3">
            <span className="h-px w-8 bg-[#ed1c28]" />
            <div>
              <div className="text-[15px] font-semibold text-white">
                Sergejs Volvenkins, PhD
              </div>
              <div className="text-[13px] text-white/65">
                Vadošais stratēģis, iMarketings.lv
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------- STATS BAR -------------------- */
const StatsBar = () => (
  <section
    data-testid="stats-section"
    className="relative py-12 md:py-16 bg-white border-y border-[#003e77]/10"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      <div className="flex items-center gap-3 mb-8">
        <span className="h-px w-10 bg-[#ed1c28]" />
        <span className="font-mono-accent text-[11px] tracking-[0.24em] uppercase text-[#003e77]/70">
          iMarketings.lv skaitļos
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
        {[
          { v: "919 MILJ. EUR", k: "Klientu ieņēmumi 2025 (GA4)" },
          { v: "200+", k: "Veiksmes stāsti" },
          { v: "LV · EU · USA", k: "Aktīvie tirgi" },
          { v: "2013", k: "Pieredze kopš" },
        ].map((s) => (
          <div key={s.k} className="border-t border-[#003e77]/15 pt-4">
            <div className="font-serif-display text-[#003e77] text-2xl md:text-[30px] lg:text-[34px] leading-tight">
              {s.v}
            </div>
            <div className="mt-2 text-[11px] md:text-[12px] uppercase tracking-[0.14em] text-[#1a1a1a]/60">
              {s.k}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-10 flex items-start gap-2.5">
        <CheckCircle2 className="h-5 w-5 text-[#25D366] mt-0.5 flex-shrink-0" />
        <span className="text-[13px] uppercase tracking-[0.16em] text-[#003e77] font-semibold">
          Ja uzsākam sadarbību, garantējam rezultātu
        </span>
      </div>
    </div>
  </section>
);

/* -------------------- AUDIT INCLUDES -------------------- */
const AuditIncludes = () => {
  const items = [
    {
      icon: Bot,
      title: "Vai AI pazīst Jūsu uzņēmumu",
      desc: "Pārbaudām, vai ChatGPT, Gemini un Google AI atpazīst Jūsu zīmolu un pakalpojumus.",
    },
    {
      icon: Search,
      title: "Pieminējumi AI atbildēs",
      desc: "Skenējam, vai Jūsu uzņēmums tiek minēts atbildēs uz tirgus jautājumiem.",
    },
    {
      icon: Target,
      title: "Salīdzinājums ar konkurentiem",
      desc: "Kā Jūs izskatāties uz konkurentu fona galvenajos vaicājumos.",
    },
    {
      icon: AlertTriangle,
      title: "Riski un neizmantotās iespējas",
      desc: "Konkrēti riski, kas pazemina redzamību, un iespējas, kas vēl nav izmantotas.",
    },
    {
      icon: TrendingUp,
      title: "Ko uzlabot AI redzamībai",
      desc: "Saraksts ar prioritāriem uzlabojumiem, lai parādītos AI sistēmu atbildēs.",
    },
    {
      icon: Compass,
      title: "Ātrākie soļi rezultātam",
      desc: "Pirmie soļi, kas dos lielāko ieguvumu īsākajā laikā.",
    },
  ];

  return (
    <section
      data-testid={LANDING.auditSection}
      className="relative py-14 md:py-20 bg-white border-y border-[#003e77]/10"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
          <div className="md:col-span-7">
            <h2 className="font-serif-display text-[#003e77] text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.02em]">
              Ko ietver bezmaksas <br /> AI SEO audits?
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-[#1a1a1a]/70 leading-relaxed text-[15px] md:text-base">
              Pēc audita Jūs saņemsiet konkrētus secinājumus un rekomendācijas.{" "}
              <span className="text-[#003e77] font-medium">
                Bez maksas. Bez saistībām.
              </span>
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#003e77]/10 border border-[#003e77]/10">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.div
                key={it.title}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                custom={i}
                variants={fadeUp}
                className="bg-white p-7 md:p-8 hover:bg-[#003e77]/[0.02] transition-colors"
              >
                <div className="flex items-start justify-between mb-5">
                  <span className="font-serif-display text-[#003e77]/35 text-[28px] leading-none">
                    0{i + 1}.
                  </span>
                  <span className="h-px w-7 bg-[#ed1c28] mt-4" />
                </div>
                <div className="flex items-center gap-3 mb-2.5">
                  <Icon className="h-5 w-5 text-[#003e77]" strokeWidth={1.7} />
                </div>
                <div className="font-serif-display text-[#003e77] text-[22px] md:text-[26px] leading-snug">
                  {it.title}
                </div>
                <p className="mt-3 text-[#1a1a1a]/70 leading-relaxed text-[15px]">
                  {it.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* -------------------- AUDIENCES -------------------- */
const Audiences = () => {
  const audiences = [
    { icon: ShoppingBag, label: "E-komercijas uzņēmumi" },
    { icon: Briefcase, label: "Pakalpojumu uzņēmumi" },
    { icon: Building2, label: "B2B uzņēmumi" },
    { icon: Globe, label: "Eksportējoši uzņēmumi" },
  ];
  return (
    <section
      data-testid={LANDING.audiencesSection}
      className="relative py-14 md:py-20"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#ed1c28]" />
            <span className="font-mono-accent text-[11px] tracking-[0.24em] uppercase text-[#003e77]/70">
              Mērķauditorija
            </span>
          </div>
          <h2 className="font-serif-display text-[#003e77] text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em]">
            Kam AI SEO audits ir aktuāls?
          </h2>
        </div>
        <div className="md:col-span-7 flex flex-wrap gap-3 md:pt-4 content-start">
          {audiences.map((a) => {
            const Icon = a.icon;
            return (
              <div
                key={a.label}
                className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full border border-[#003e77]/20 bg-white hover:border-[#003e77] hover:bg-[#003e77]/[0.03] transition-colors"
              >
                <Icon className="h-4 w-4 text-[#003e77]" strokeWidth={1.7} />
                <span className="text-[#1a1a1a]/85 text-[15px]">{a.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* -------------------- FORM -------------------- */
const ApplyForm = () => {
  const [form, setForm] = useState({
    name: "",
    website: "",
    email: "",
    phone: "",
    company: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return "Lūdzu, ievadiet vārdu.";
    if (!form.website.trim()) return "Lūdzu, ievadiet mājaslapas adresi.";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Lūdzu, ievadiet derīgu e-pasta adresi.";
    if (!form.phone.trim()) return "Lūdzu, ievadiet tālruņa numuru.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);

      // --- Conversion tracking ---
      const eventId = `lead_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq(
          "trackCustom",
          "aiseo_lead",
          {
            content_name: "AI SEO Audit",
            content_category: "Lead Form",
            currency: "EUR",
            value: 1,
          },
          { eventID: eventId }
        );
      }
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "aiseo_lead", {
          event_category: "lead",
          event_label: "AI SEO Audit Lead",
          form_location: "landing",
          transaction_id: eventId,
        });
      }
      // --- /Conversion tracking ---

      setSuccess(true);
      toast.success(
        "Paldies! Mēs ar Jums sazināsimies tuvāko darba dienu laikā."
      );
      setForm({ name: "", website: "", email: "", phone: "", company: "" });
    } catch (err) {
      console.error(err);
      toast.error(
        "Diemžēl pieteikumu nosūtīt neizdevās. Lūdzu, mēģiniet vēlreiz."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="pieteikties"
      data-testid={LANDING.formSection}
      className="relative py-14 md:py-24 bg-[#003e77] text-white overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-[480px] h-[480px] rounded-full bg-[#ed1c28] opacity-[0.08] blur-[120px] -translate-y-1/3 translate-x-1/3" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#ed1c28]" />
            <span className="font-mono-accent text-[11px] tracking-[0.24em] uppercase text-white/70">
              Pieteikties
            </span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-[56px] leading-[1.04] tracking-[-0.02em]">
            Pieteikties bezmaksas <br />
            AI&nbsp;SEO auditam
          </h2>
          <p className="mt-6 text-white/75 leading-relaxed text-[16px] md:text-[17px]">
            Bezmaksas AI SEO auditu nodrošinām uzņēmumiem, kuru apgrozījums
            pārsniedz 500&nbsp;000 EUR gadā.
          </p>

          <div className="mt-10 hidden lg:block">
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.18em] text-white/50 mb-3">
              Kontakti
            </div>
            <div className="space-y-2 text-[15px]">
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick("form-side")}
                className="inline-flex items-center gap-2 text-white/85 hover:text-white"
              >
                <WhatsappIcon className="h-4 w-4 text-[#25D366]" /> WhatsApp
              </a>
              <div>
                <a
                  href={TEL_URL}
                  onClick={() => trackWhatsAppClick("form-side-phone")}
                  className="inline-flex items-center gap-2 text-white/85 hover:text-white"
                >
                  <Phone className="h-4 w-4 text-[#ed1c28]" /> {WA_NUMBER_DISPLAY}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="bg-white text-[#1a1a1a] p-6 md:p-10 rounded-[4px] shadow-[0_30px_60px_-20px_rgba(0,62,119,0.35)]">
            {success ? (
              <div className="text-center py-10">
                <div className="mx-auto h-14 w-14 rounded-full bg-[#003e77]/10 border border-[#003e77]/20 flex items-center justify-center">
                  <CheckCircle2 className="h-7 w-7 text-[#003e77]" />
                </div>
                <h3 className="mt-6 font-serif-display text-[#003e77] text-2xl md:text-3xl">
                  Paldies, pieteikums saņemts!
                </h3>
                <p className="mt-3 text-[#1a1a1a]/70 max-w-md mx-auto">
                  Mēs ar Jums sazināsimies tuvāko darba dienu laikā uz norādīto
                  e-pastu vai tālruni.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  className="mt-8 inline-flex items-center gap-2 text-sm text-[#003e77] hover:underline"
                >
                  Iesniegt vēl vienu pieteikumu
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label
                      htmlFor="name"
                      className="text-[#1a1a1a]/80 mb-2 inline-block text-[13px]"
                    >
                      Vārds <span className="text-[#ed1c28]">*</span>
                    </Label>
                    <Input
                      id="name"
                      data-testid={LANDING.formNameInput}
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jānis Bērziņš"
                      className="h-11 bg-white border-[#003e77]/20 text-[#1a1a1a] placeholder:text-[#1a1a1a]/35 focus-visible:ring-[#003e77] focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-[#003e77] rounded-[3px]"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="website"
                      className="text-[#1a1a1a]/80 mb-2 inline-block text-[13px]"
                    >
                      Mājaslapa <span className="text-[#ed1c28]">*</span>
                    </Label>
                    <Input
                      id="website"
                      data-testid={LANDING.formWebsiteInput}
                      value={form.website}
                      onChange={update("website")}
                      placeholder="https://example.lv"
                      className="h-11 bg-white border-[#003e77]/20 text-[#1a1a1a] placeholder:text-[#1a1a1a]/35 focus-visible:ring-[#003e77] focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-[#003e77] rounded-[3px]"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label
                      htmlFor="email"
                      className="text-[#1a1a1a]/80 mb-2 inline-block text-[13px]"
                    >
                      E-pasts <span className="text-[#ed1c28]">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid={LANDING.formEmailInput}
                      value={form.email}
                      onChange={update("email")}
                      placeholder="janis@example.lv"
                      className="h-11 bg-white border-[#003e77]/20 text-[#1a1a1a] placeholder:text-[#1a1a1a]/35 focus-visible:ring-[#003e77] focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-[#003e77] rounded-[3px]"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="phone"
                      className="text-[#1a1a1a]/80 mb-2 inline-block text-[13px]"
                    >
                      Tālrunis <span className="text-[#ed1c28]">*</span>
                    </Label>
                    <Input
                      id="phone"
                      data-testid={LANDING.formPhoneInput}
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+371 12 345 678"
                      className="h-11 bg-white border-[#003e77]/20 text-[#1a1a1a] placeholder:text-[#1a1a1a]/35 focus-visible:ring-[#003e77] focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-[#003e77] rounded-[3px]"
                    />
                  </div>
                </div>
                <div>
                  <Label
                    htmlFor="company"
                    className="text-[#1a1a1a]/80 mb-2 inline-block text-[13px]"
                  >
                    Uzņēmuma nosaukums
                  </Label>
                  <Input
                    id="company"
                    data-testid={LANDING.formCompetitorsInput}
                    value={form.company}
                    onChange={update("company")}
                    placeholder="SIA Piemērs"
                    className="h-11 bg-white border-[#003e77]/20 text-[#1a1a1a] placeholder:text-[#1a1a1a]/35 focus-visible:ring-[#003e77] focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-[#003e77] rounded-[3px]"
                  />
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid={LANDING.formSubmitBtn}
                    className="h-12 px-6 bg-[#ed1c28] text-white font-medium hover:bg-[#c8141e] rounded-[3px] transition-all disabled:opacity-60"
                  >
                    {loading ? "Sūta..." : "Pieteikties bezmaksas auditam"}
                    {!loading && <ArrowUpRight className="ml-1.5 h-5 w-5" />}
                  </Button>
                  <p className="text-[12px] text-[#1a1a1a]/55 leading-relaxed">
                    Iesniedzot formu, Jūs piekrītat, ka iMarketings.lv
                    sazināsies ar Jums par AI SEO auditu. Bez SPAM. Bez
                    saistībām.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------- BENEFITS -------------------- */
const Benefits = () => {
  const benefits = [
    {
      icon: Sparkles,
      title: "Lielāka redzamība",
      desc: "Ļauj palielināt iespēju, ka klienti sastop Jūsu uzņēmumu ne tikai Google meklētājā, bet arī AI platformās.",
    },
    {
      icon: Shield,
      title: "Konkurences priekšrocība",
      desc: "Kamēr daudzi uzņēmumi vēl tikai sāk interesēties par AI SEO, Jūs varat sākt veidot pozīcijas jau tagad.",
    },
    {
      icon: TrendingUp,
      title: "Sagatavotība nākotnei",
      desc: "Lietotāju paradumi mainās. Uzņēmumi, kas pielāgosies pirmie, iegūs lielāko ieguvumu.",
    },
  ];
  return (
    <section
      data-testid={LANDING.benefitsSection}
      className="relative py-14 md:py-20 bg-white border-t border-[#003e77]/10"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-[#ed1c28]" />
          <span className="font-mono-accent text-[11px] tracking-[0.24em] uppercase text-[#003e77]/70">
            Kāpēc tas ir svarīgs
          </span>
        </div>
        <h2 className="font-serif-display text-[#003e77] text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.02em] max-w-3xl">
          Kāpēc AI SEO ir svarīgs?
        </h2>

        <div className="mt-12 grid md:grid-cols-3 gap-0 md:gap-0 border-l border-t border-[#003e77]/10 md:border-l-0 md:border-t-0">
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.title}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                custom={i}
                variants={fadeUp}
                className="relative p-7 md:p-10 md:border-l border-[#003e77]/10 first:md:border-l-0 border-b md:border-b-0 last:border-b-0"
              >
                <div className="flex items-start justify-between mb-6">
                  <Icon className="h-7 w-7 text-[#003e77]" strokeWidth={1.5} />
                  <span className="font-serif-display text-[#003e77]/30 text-2xl leading-none">
                    0{i + 1}.
                  </span>
                </div>
                <div className="font-serif-display text-[#003e77] text-2xl md:text-[28px] leading-snug">
                  {b.title}
                </div>
                <p className="mt-3 text-[#1a1a1a]/70 leading-relaxed text-[15px]">
                  {b.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* -------------------- EXAMPLE (ChatGPT screenshot) -------------------- */
const Example = () => (
  <section className="relative py-14 md:py-20 bg-[#fafaf7] border-t border-[#003e77]/10">
    <div className="max-w-5xl mx-auto px-5 md:px-10">
      <div className="flex items-center gap-3 mb-6">
        <span className="h-px w-10 bg-[#ed1c28]" />
        <span className="font-mono-accent text-[11px] tracking-[0.24em] uppercase text-[#003e77]/70">
          Reāls piemērs
        </span>
      </div>
      <h2 className="font-serif-display text-[#003e77] text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em]">
        Tā tas izskatās praksē
      </h2>

      <motion.figure
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6 }}
        className="mt-10"
      >
        <div className="rounded-[6px] border border-[#003e77]/15 bg-white p-2 md:p-3 shadow-[0_30px_60px_-25px_rgba(0,62,119,0.3)]">
          <img
            src="/assets/chatgpt.webp"
            alt="ChatGPT atbilde, kurā rekomendēts iMarketings"
            className="w-full h-auto rounded-[4px]"
            loading="lazy"
          />
        </div>
        <figcaption className="mt-4 text-[13px] italic text-[#1a1a1a]/55 leading-relaxed">
          ChatGPT, 2026. gada jūlijs. Jautājums redzams attēlā. AI atbildes var
          mainīties.
        </figcaption>
      </motion.figure>
    </div>
  </section>
);

/* -------------------- FAQ -------------------- */
const FAQ = () => {
  const faqs = [
    {
      q: "Kas ir AI SEO un ar ko tas atšķiras no parastā SEO?",
      a: (
        <>
          <p>
            SEO uzlabo pozīciju Google saišu sarakstā, no kura lietotājs
            izvēlas pats. AI SEO (arī AEO — Answer Engine Optimization)
            nodrošina, ka Jūs esat starp tiem dažiem uzņēmumiem, ko AI nosauc
            vienā atbildē — ChatGPT, Claude, Gemini, Perplexity un Google AI
            Overviews.
          </p>
          <p className="mt-3">
            Mūsdienu sistēmas vairs nerāda tikai saišu sarakstus, bet tās
            apkopo informāciju un sniedz konkrētas atbildes. AI SEO neaizstāj
            SEO, tas to papildina.
          </p>
        </>
      ),
    },
    {
      q: "Ko tieši es saņemšu bezmaksas auditā?",
      a: (
        <>
          <p>
            Pārbaudām, vai ChatGPT, Claude, Gemini, Perplexity un Google AI
            Overviews pazīst Jūsu uzņēmumu un ko tie par Jums saka. Parādām, uz
            kuriem Jūsu nozares jautājumiem AI iesaka konkurentus Jūsu vietā.
            Sniedzam prioritizētu sarakstu ar konkrētiem uzlabojumiem, sākot ar
            tiem, kas dos rezultātu ātrāk.
          </p>
          <p className="mt-3">
            Rezultātus saņemsiet 3 darba dienu laikā un prezentēsim tos ~40
            minūšu sarunā.
          </p>
        </>
      ),
    },
    {
      q: "Vai audits tiešām ir bez maksas un bez saistībām?",
      a: (
        <p>
          Jā. Mēs to darām, jo lielākā daļa uzņēmumu nezina savu situāciju AI
          atbildēs, un neapzinās aktuālās patērētāju tendences. Auditu veicam
          uzņēmumiem ar apgrozījumu no 500 000 EUR gadā — mazākiem uzņēmumiem
          ieguldījums parasti vēl neatmaksājas.
        </p>
      ),
    },
    {
      q: "Kāpēc AI SEO ir svarīgs jau tagad?",
      a: (
        <>
          <p>Trīs iemesli darbojas vienlaikus:</p>
          <ul className="mt-3 space-y-2 list-disc pl-5">
            <li>
              Lietotāji arvien biežāk nesaņem rezultātu sarakstu, bet vienu
              konkrētu atbildi
            </li>
            <li>AI rīki izvēlas tikai dažus avotus, nevis desmitiem</li>
            <li>
              Ja Jūsu zīmols nav starp tiem, tas vienkārši neparādās šajā vidē
            </li>
          </ul>
          <p className="mt-3">
            Ceturtais iemesls ir laiks. Šobrīd konkurence šajā jomā vēl ir maza
            — pēc gada tā tāda vairs nebūs.
          </p>
        </>
      ),
    },
    {
      q: "Vai AI SEO atmaksājas, ja no AI nāk maz apmeklējumu?",
      a: (
        <p>
          Apmeklējumu skaits ir maldinošs rādītājs. Jaunākie pētījumi rāda, ka
          līdz 60% meklējumu beidzas bez klikšķa — cilvēks saņem atbildi un
          neapmeklē nevienu mājaslapu. AI Jūs var ieteikt, klients var atnākt
          vēlāk tieši vai caur Google, un GA4 to nekad neatzīmēs kā AI avotu.
        </p>
      ),
    },
    {
      q: "Kas notiek pēc tam, kad piesakos?",
      a: (
        <p>
          Sazināsimies vienas darba dienas laikā, lai precizētu pāris
          jautājumus. Auditu sagatavojam 3 darba dienu laikā un prezentējam
          sarunā, kur varat uzdot jautājumus. Ja vēlaties turpināt, sagatavosim
          AEO stratēģiju tieši Jūsu nozarei. Ja nē — audits paliek Jums.
        </p>
      ),
    },
  ];

  return (
    <section
      data-testid="faq-section"
      className="relative py-14 md:py-20 bg-[#fafaf7] border-t border-[#003e77]/10"
    >
      <div className="max-w-3xl mx-auto px-5 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-[#ed1c28]" />
          <span className="font-mono-accent text-[11px] tracking-[0.24em] uppercase text-[#003e77]/70">
            Biežāk uzdotie jautājumi
          </span>
        </div>
        <h2 className="font-serif-display text-[#003e77] text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] mb-10">
          Jautājumi un atbildes
        </h2>

        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="border-b border-[#003e77]/15"
            >
              <AccordionTrigger className="py-5 text-left hover:no-underline text-[17px] md:text-[19px] font-semibold text-[#003e77] [&>svg]:text-[#003e77]">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-[15px] md:text-base text-[#1a1a1a]/75 leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

/* -------------------- FOOTER -------------------- */
const Footer = () => (
  <footer className="bg-[#003e77] text-white">
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-12">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex items-center gap-3">
          <img
            src="/favicon.png"
            alt="iMarketings.lv"
            className="h-7 w-7 brightness-0 invert"
          />
          <span className="font-serif-display text-[19px]">iMarketings.lv</span>
          <span className="hidden md:inline text-white/40 text-[11px] uppercase tracking-[0.18em] font-mono-accent ml-2">
            · AI SEO Lab
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-5 text-sm">
          <a
            data-testid={LANDING.footerWhatsapp}
            href={WA_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick("footer")}
            aria-label="Chat in WhatsApp"
            className="inline-flex items-center justify-center h-9 w-9 rounded-[3px] border border-white/15 hover:bg-white/5"
          >
            <WhatsappIcon className="h-4 w-4 text-[#25D366]" />
          </a>
          <a
            data-testid={LANDING.footerPhone}
            href={TEL_URL}
            onClick={() => trackWhatsAppClick("footer-phone")}
            className="inline-flex items-center gap-2 text-white/85 hover:text-white"
          >
            <Phone className="h-4 w-4 text-[#ed1c28]" /> {WA_NUMBER_DISPLAY}
          </a>
          <span className="text-white/55 text-[13px]">
            © {new Date().getFullYear()} iMarketings.lv
          </span>
        </div>
      </div>
    </div>
  </footer>
);

/* -------------------- STICKY WA -------------------- */
const StickyWhatsapp = () => (
  <a
    data-testid={LANDING.whatsappStickyBtn}
    href={WA_URL}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => trackWhatsAppClick("sticky")}
    aria-label="Chat in WhatsApp"
    className="fixed bottom-6 left-6 z-50 inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#25D366] text-white shadow-[0_10px_40px_-5px_rgba(37,211,102,0.55)] hover:bg-[#1ebe57] transition-all hover:scale-[1.06] float-y"
  >
    <WhatsappIcon className="h-7 w-7" />
  </a>
);

/* -------------------- PAGE -------------------- */
export default function Landing() {
  return (
    <div className="min-h-screen bg-[#fafaf7] text-[#1a1a1a]">
      <Nav />
      <main>
        <Hero />
        <Quote />
        <AuditIncludes />
        <Audiences />
        <ApplyForm />
        <Benefits />
        <Example />
        <FAQ />
      </main>
      <Footer />
      <StickyWhatsapp />
    </div>
  );
}
